// Espace de diagnostic (écran clé, mobile d'abord) : une décision à la fois, pistes sourcées, chronologie lisible,
// mesures avec unités, clôture résolue ou escaladée. Enregistrement automatique avec état visible.
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileText, FlaskConical, Lock, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, EntetePage, EtatErreur, EtatVide, FilContexte,
  IndicateurEnregistrement, LienBouton, Panneau, Valeur, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { BadgeStatutIntervention } from '../../components/statuts.tsx';
import { BadgeOrigine, EtiquetteType, LegendeDiagnostic, LienInterventionSource, SourceExtrait } from './composants.tsx';
import { PanneauPistes } from './PanneauPistes.tsx';
import { PanneauDejaVu } from './PanneauDejaVu.tsx';
import { PanneauHistoriqueEquipement } from '../../components/Carnet.tsx';
import { useEnregistrementAuto } from './useEnregistrementAuto.ts';
import { decouperCodes } from './PageNouvelleIntervention.tsx';
import {
  DEPUIS, LIBELLES_STATUT_ELEMENT, TYPES_LIEU, UNITES, valeurFr, type Element, type Intervention, type TypeElement,
} from './types.ts';

type Appliquer = (r: Intervention) => void;

function useActions(i: Intervention | undefined, appliquer: Appliquer) {
  const notifier = useNotifier();
  const [occupe, setOccupe] = useState<string | null>(null);
  const executer = async (cle: string, f: () => Promise<Intervention>, message?: string) => {
    setOccupe(cle);
    try { appliquer(await f()); if (message) notifier(message); return true; }
    catch (e) { notifier(messageErreur(e), true); return false; }
    finally { setOccupe(null); }
  };
  const id = i?.id;
  return {
    occupe,
    ajouter: (corps: Record<string, unknown>, message = 'Ajouté au diagnostic.') =>
      executer('ajouter', async () => (await api.post<{ intervention: Intervention }>(`/api/interventions/${id}/elements`, corps)).intervention, message),
    majElement: (eid: string, corps: Record<string, unknown>, message?: string) =>
      executer(`el-${eid}`, async () => (await api.patch<{ intervention: Intervention }>(`/api/interventions/${id}/elements/${eid}`, corps)).intervention, message),
    supprimer: (eid: string) => executer(`del-${eid}`, () => api.del<Intervention>(`/api/interventions/${id}/elements/${eid}`), 'Élément supprimé.'),
    ordonner: (ids: string[]) => executer('ordre', () => api.post<Intervention>(`/api/interventions/${id}/elements/ordre`, { ids })),
    statut: (corps: Record<string, unknown>, message: string) => executer('statut', () => api.patch<Intervention>(`/api/interventions/${id}`, corps), message),
  };
}
type Actions = ReturnType<typeof useActions>;

export function PageIntervention() {
  const { id } = useParams();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { peut } = useSession();
  const q = useQuery({ queryKey: ['intervention', id], queryFn: () => api.get<Intervention>(`/api/interventions/${id}`) });
  const appliquer: Appliquer = (r) => { qc.setQueryData(['intervention', id], r); qc.invalidateQueries({ queryKey: ['interventions'] }); };
  const auto = useEnregistrementAuto(q.data, appliquer, (m) => notifier(m, true));
  const actions = useActions(q.data, appliquer);
  const [focus, setFocus] = useState<string | null>(null);
  const [symptomeOuvert, setSymptomeOuvert] = useState(false);
  const carte = useRef<HTMLDivElement>(null);
  const saisie = useRef<HTMLDivElement>(null);
  const [typeSaisie, setTypeSaisie] = useState<TypeElement>('mesure');

  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const i = q.data;
  const ecrire = peut('intervention.ecrire') && i.modifiable;
  const securite = i.elements.filter((e) => e.kind === 'securite');
  const eq = i.equipement;
  const versionInconnue = !!eq && !eq.sw_version && !eq.firmware;

  const allerCarte = (eid?: string) => { if (eid) setFocus(eid); carte.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const allerSaisie = (t: TypeElement) => { setTypeSaisie(t); saisie.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div className="pile-l int-espace">
      <EntetePage
        fil={<FilContexte noeuds={[
          { type: 'Site', libelle: i.site.name, to: `/sites/${i.site.id}` },
          ...(eq?.chemin ?? []).map((c) => ({ type: TYPES_LIEU[c.kind] ?? c.kind, libelle: c.name, to: `/sites/${i.site.id}` })),
          ...(eq ? [{ type: 'Équipement', libelle: eq.label, to: `/sites/equipements/${eq.id}` }] : []),
          { type: 'Intervention', libelle: i.reference },
        ]} />}
        titre={<span className="rangee" style={{ gap: 10 }}>{auto.valeurs?.title ?? i.title}
          {i.site.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}</span>}
        sousTitre={<span className="rangee" style={{ gap: 8 }}>
          <span className="mono">{i.reference}</span><BadgeStatutIntervention statut={i.status} />
          <span className="expert-seulement masque-etroit">{i.intervenant ? `${i.intervenant.nom}, ` : ''}ouverte le {dateHeure(i.created_at)}</span>
          {ecrire && <IndicateurEnregistrement etat={auto.etat} quand={auto.quand} />}
        </span>}
        actions={<span className="masque-etroit"><LienBouton to={`/interventions/${i.id}/rapport`} icone={<FileText aria-hidden />}>Rapport</LienBouton></span>} />

      {securite.map((s) => <Alerte key={s.id} ton="ambre" titre="Sécurité">{s.text}</Alerte>)}
      {!i.modifiable && <Alerte ton="info" titre="Intervention clôturée">Consultation seule. Le rapport reste téléchargeable.</Alerte>}

      <div className="int-disposition">
        <div className="int-principal">
          <div ref={carte} className="int-o-etape">
            <CarteEtape i={i} focus={focus} setFocus={setFocus} actions={actions} ecrire={ecrire}
              ouvrirSymptome={() => setSymptomeOuvert(true)} allerSaisie={allerSaisie} />
          </div>
          <EditeurSymptome i={i} auto={auto} ecrire={ecrire} ouvert={symptomeOuvert || !i.symptom} basculer={setSymptomeOuvert} appliquer={appliquer} />
          {ecrire && <div ref={saisie} className="int-o-saisie"><Saisie type={typeSaisie} setType={setTypeSaisie} actions={actions} /></div>}
          <Chronologie i={i} actions={actions} ecrire={ecrire} traiter={allerCarte} />
          {peut('intervention.ecrire') && <Cloture i={i} actions={actions} />}
        </div>
        <div className="int-cote">
          <Identification i={i} versionInconnue={versionInconnue} />
          <PanneauDejaVu intervention={i} appliquer={appliquer} />
          {eq && <PanneauHistoriqueEquipement equipementId={eq.id} interventionId={i.id} />}
          <PanneauPistes intervention={i} appliquer={appliquer} />
        </div>
      </div>
    </div>
  );
}

// ─── Identification de l'équipement : inconnues visibles, alerte si version inconnue ───
function Identification({ i, versionInconnue }: { i: Intervention; versionInconnue: boolean }) {
  const eq = i.equipement;
  const [ouvert] = useState(() => typeof window === 'undefined' || window.innerWidth > 900);
  if (!eq) {
    return <div className="int-o-ident"><Alerte ton="ambre" titre="Aucun équipement désigné">Les pistes documentées s’appuient sur la référence exacte : désignez l’équipement dans « Symptôme et contexte ».</Alerte></div>;
  }
  return (
    <details className="panneau int-identification int-o-ident" open={ouvert}>
      <summary>
        <span className="int-identification-titre">{eq.label}</span>
        <span className="petit secondaire"><Valeur v={eq.reference} mono inconnu="Référence inconnue" /></span>
        {versionInconnue && <Badge ton="ambre">Version inconnue</Badge>}
        <ChevronDown aria-hidden className="int-chevron" />
      </summary>
      <div className="panneau-corps">
        <dl className="fiche int-fiche">
          <div><dt>Fabricant</dt><dd><Valeur v={eq.manufacturer} /></dd></div>
          <div><dt>Gamme</dt><dd><Valeur v={eq.range_name} /></dd></div>
          <div><dt>Référence exacte</dt><dd><Valeur v={eq.reference} mono /></dd></div>
          <div><dt>Version logicielle</dt><dd><Valeur v={eq.sw_version} mono /></dd></div>
          <div><dt>Firmware</dt><dd><Valeur v={eq.firmware} mono /></dd></div>
          <div className="expert-seulement"><dt>Version matérielle</dt><dd><Valeur v={eq.hw_version} mono /></dd></div>
          <div className="expert-seulement"><dt>Protocoles</dt><dd><Valeur v={eq.protocols} /></dd></div>
          <div className="expert-seulement"><dt>Adresse réseau / bus</dt><dd><Valeur v={eq.network_address} mono /></dd></div>
        </dl>
        {versionInconnue && (
          <div style={{ marginTop: 12 }}>
            <Alerte ton="ambre" titre="Version logicielle et firmware inconnus">La correspondance des sources avec cet équipement ne peut pas être confirmée. Relevez-les sur l’équipement ou son logiciel.</Alerte>
          </div>
        )}
        {(!eq.reference || !eq.manufacturer) && (
          <div style={{ marginTop: 12 }}>
            <Alerte ton="ambre" titre="Identification incomplète">Sans fabricant et référence exacte, la documentation ne peut pas être rattachée avec certitude.</Alerte>
          </div>
        )}
        <div style={{ marginTop: 12 }}><LienBouton compact variante="fantome" to={`/sites/equipements/${eq.id}`}>Fiche de l’équipement</LienBouton></div>
      </div>
    </details>
  );
}

// ─── Prochaine étape : UNE décision à la fois ───
type Etape =
  | { type: 'cloturee' } | { type: 'terminee' } | { type: 'symptome' } | { type: 'pistes' } | { type: 'suite' }
  | { type: 'controle'; el: Element; reste: number } | { type: 'hypothese'; el: Element } | { type: 'resoudre'; cause: Element };

function prochaineEtape(i: Intervention, focus: string | null): Etape {
  if (i.status === 'cloturee') return { type: 'cloturee' };
  if (i.status === 'resolue' || i.status === 'escaladee') return { type: 'terminee' };
  const controles = i.elements.filter((e) => e.kind === 'controle_propose' && e.status === 'a_faire');
  const choisi = focus ? i.elements.find((e) => e.id === focus) : null;
  if (choisi?.kind === 'controle_propose' && choisi.status === 'a_faire') return { type: 'controle', el: choisi, reste: controles.length };
  if (choisi?.kind === 'hypothese' && !choisi.status) return { type: 'hypothese', el: choisi };
  if (!i.symptom?.trim() && !(i.context?.codes ?? []).length) return { type: 'symptome' };
  if (controles.length) return { type: 'controle', el: controles[0], reste: controles.length };
  const hyp = i.elements.find((e) => e.kind === 'hypothese' && !e.status);
  if (hyp) return { type: 'hypothese', el: hyp };
  const cause = i.elements.find((e) => e.kind === 'cause_confirmee');
  if (cause) return { type: 'resoudre', cause };
  if (!i.elements.some((e) => e.kind === 'controle_propose')) return { type: 'pistes' };
  return { type: 'suite' };
}

function CarteEtape({ i, focus, setFocus, actions, ecrire, ouvrirSymptome, allerSaisie }: {
  i: Intervention; focus: string | null; setFocus: (v: string | null) => void; actions: Actions; ecrire: boolean;
  ouvrirSymptome: () => void; allerSaisie: (t: TypeElement) => void;
}) {
  const etape = prochaineEtape(i, focus);
  const [resultat, setResultat] = useState('');
  const cle = 'el' in etape ? etape.el.id : etape.type;
  // Deux temps : d'abord la décision (Fait / Non fait / Sans objet), puis le résultat constaté.
  const [enAttente, setEnAttente] = useState<null | 'fait' | 'non_fait'>(null);
  useEffect(() => { setResultat(''); setEnAttente(null); }, [cle]);
  const aller = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const traiter = async (statut: 'fait' | 'non_fait' | 'sans_objet') => {
    if (etape.type !== 'controle') return;
    const ok = await actions.majElement(etape.el.id, { status: statut, resultat: resultat.trim() || null },
      statut === 'fait' ? 'Contrôle réalisé enregistré.' : 'Contrôle mis à jour.');
    if (ok) setFocus(null);
  };
  const decider = async (statut: 'confirmee' | 'ecartee') => {
    if (etape.type !== 'hypothese') return;
    const motif = resultat.trim();
    const corps: Record<string, unknown> = { status: statut };
    if (motif && statut === 'ecartee') corps.justification = [etape.el.justification, `Écartée : ${motif}`].filter(Boolean).join('\n');
    if (motif && statut === 'confirmee') corps.resultat = motif;
    const ok = await actions.majElement(etape.el.id, corps, statut === 'confirmee' ? 'Cause confirmée.' : 'Hypothèse écartée.');
    if (ok) setFocus(null);
  };

  let contenu;
  if (etape.type === 'controle') {
    const el = etape.el;
    contenu = (
      <>
        <p className="int-etape-texte">{el.text}</p>
        {el.source && <SourceExtrait versionId={el.source.version_id} page={el.source.page} chunkId={el.source.chunk_id} titre={el.source.titre} version={el.source.version_label} />}
        {ecrire && !enAttente && (
          <div className="groupe-btn int-decision">
            <Bouton variante="principal" onClick={() => setEnAttente('fait')} disabled={!!actions.occupe}>Fait</Bouton>
            <Bouton onClick={() => setEnAttente('non_fait')} disabled={!!actions.occupe}>Non fait</Bouton>
            <Bouton onClick={() => traiter('sans_objet')} chargement={actions.occupe === `el-${el.id}`}>Sans objet</Bouton>
          </div>
        )}
        {ecrire && enAttente && (
          <div className="pile int-resultat">
            <ChampTexte libelle={enAttente === 'fait' ? 'Résultat constaté' : 'Pourquoi n’a-t-il pas été réalisé ?'} rows={2} autoFocus value={resultat}
              onChange={(e) => setResultat(e.target.value)}
              placeholder={enAttente === 'fait' ? 'Valeur relevée, état, conformité' : 'Accès impossible, matériel manquant…'}
              aide="Facultatif, mais utile au rapport." />
            <div className="groupe-btn int-decision">
              <Bouton variante="principal" chargement={actions.occupe === `el-${el.id}`} onClick={() => traiter(enAttente)}>
                {enAttente === 'fait' ? 'Enregistrer le contrôle réalisé' : 'Enregistrer comme non fait'}</Bouton>
              <Bouton onClick={() => setEnAttente(null)}>Retour</Bouton>
            </div>
          </div>
        )}
        {el.justification && <details className="int-details"><summary>Justification</summary><p className="petit" style={{ whiteSpace: 'pre-wrap' }}>{el.justification}</p></details>}
        {ecrire && <div><Bouton compact variante="fantome" onClick={() => allerSaisie('mesure')}>Saisir une mesure</Bouton></div>}
      </>
    );
  } else if (etape.type === 'hypothese') {
    const el = etape.el;
    contenu = (
      <>
        <EtiquetteType kind="hypothese" />
        <p className="int-etape-texte">{el.text}</p>
        {el.source && <SourceExtrait versionId={el.source.version_id} page={el.source.page} chunkId={el.source.chunk_id} titre={el.source.titre} version={el.source.version_label} />}
        {el.source_intervention && <LienInterventionSource s={el.source_intervention} />}
        {el.justification && <details className="int-details"><summary>Justification</summary><p className="petit" style={{ whiteSpace: 'pre-wrap' }}>{el.justification}</p></details>}
        {ecrire && (
          <>
            <ChampTexte libelle="Ce qui confirme ou écarte cette hypothèse" rows={2} value={resultat} onChange={(e) => setResultat(e.target.value)} />
            <div className="groupe-btn int-decision">
              <Bouton variante="principal" chargement={actions.occupe === `el-${el.id}`} onClick={() => decider('confirmee')}>Confirmer la cause</Bouton>
              <Bouton onClick={() => decider('ecartee')} disabled={!!actions.occupe}>Écarter</Bouton>
            </div>
          </>
        )}
      </>
    );
  } else if (etape.type === 'symptome') {
    contenu = (<>
      <p className="int-etape-texte">Décrivez le symptôme et les codes défaut affichés.</p>
      <p className="petit secondaire">Ils servent à retrouver les pistes dans la documentation.</p>
      {ecrire && <div className="groupe-btn int-decision"><Bouton variante="principal" onClick={() => { ouvrirSymptome(); aller('symptome'); }}>Décrire le symptôme</Bouton></div>}
    </>);
  } else if (etape.type === 'pistes') {
    contenu = (<>
      <p className="int-etape-texte">Consultez les pistes documentées et retenez les contrôles à faire.</p>
      <p className="petit secondaire">Chaque contrôle garde sa source, consultable à la bonne page. Vous pouvez aussi noter une hypothèse ou une observation.</p>
      <div className="groupe-btn int-decision">
        <Bouton variante="principal" onClick={() => aller('pistes')}>Voir les pistes</Bouton>
        {ecrire && <Bouton onClick={() => allerSaisie('hypothese')}>Noter une hypothèse</Bouton>}
      </div>
    </>);
  } else if (etape.type === 'suite') {
    contenu = (<>
      <p className="int-etape-texte">Les contrôles proposés sont traités, sans cause confirmée.</p>
      <p className="petit secondaire">Formulez une hypothèse, cherchez d’autres pistes, ou escaladez si le diagnostic dépasse ce qui est faisable sur place.</p>
      {ecrire && <div className="groupe-btn int-decision">
        <Bouton variante="principal" onClick={() => allerSaisie('hypothese')}>Noter une hypothèse</Bouton>
        <Bouton onClick={() => aller('pistes')}>Voir les pistes</Bouton>
        <Bouton onClick={() => aller('cloture')}>Escalader</Bouton>
      </div>}
    </>);
  } else if (etape.type === 'resoudre') {
    contenu = (<>
      <EtiquetteType kind="cause_confirmee" />
      <p className="int-etape-texte">{etape.cause.text}</p>
      <p className="petit secondaire">Consignez les opérations réalisées, puis déclarez l’intervention résolue.</p>
      {ecrire && <div className="groupe-btn int-decision">
        <Bouton variante="principal" onClick={() => aller('cloture')}>Déclarer résolue</Bouton>
        <Bouton onClick={() => allerSaisie('operation')}>Noter une opération</Bouton>
      </div>}
    </>);
  } else if (etape.type === 'terminee') {
    contenu = (<>
      <p className="int-etape-texte">{i.status === 'resolue' ? 'Intervention résolue.' : 'Intervention escaladée.'}</p>
      <p className="petit secondaire">Relisez le rapport, puis clôturez : l’intervention ne sera plus modifiable.</p>
      <div className="groupe-btn int-decision">
        <LienBouton variante="principal" to={`/interventions/${i.id}/rapport`}>Voir le rapport</LienBouton>
        <Bouton onClick={() => aller('cloture')}>Clôturer</Bouton>
      </div>
    </>);
  } else {
    contenu = (<>
      <p className="int-etape-texte"><Lock size={18} aria-hidden style={{ display: 'inline', verticalAlign: '-3px' }} /> Intervention clôturée le {dateHeure(i.closed_at)}.</p>
      <div className="groupe-btn int-decision"><LienBouton variante="principal" to={`/interventions/${i.id}/rapport`}>Voir le rapport</LienBouton></div>
    </>);
  }

  return (
    <section className="panneau int-carte" aria-labelledby="int-carte-titre">
      <div className="int-carte-entete">
        <h2 id="int-carte-titre">Prochaine étape</h2>
        {etape.type === 'controle' && <span className="rangee int-carte-types">
          <EtiquetteType kind="controle_propose" /><BadgeOrigine el={etape.el} />
          <span className="tres-petit secondaire">{etape.reste > 1 ? `${etape.reste} à faire` : 'Dernier à faire'}</span></span>}
        {focus && <Bouton compact variante="fantome" onClick={() => setFocus(null)}>Revenir à l’ordre proposé</Bouton>}
      </div>
      <div className="int-carte-corps pile">{contenu}</div>
    </section>
  );
}

// ─── Symptôme et contexte (enregistrement automatique) ───
function EditeurSymptome({ i, auto, ecrire, ouvert, basculer, appliquer }: {
  i: Intervention; auto: ReturnType<typeof useEnregistrementAuto>; ecrire: boolean; ouvert: boolean; basculer: (v: boolean) => void; appliquer: Appliquer;
}) {
  const v = auto.valeurs;
  const notifier = useNotifier();
  const [codesTexte, setCodesTexte] = useState(() => (i.context?.codes ?? []).join(', '));
  const site = useQuery({ queryKey: ['site', i.site_id], enabled: ecrire && ouvert, queryFn: () => api.get<{ equipements: { id: string; label: string; reference: string | null }[] }>(`/api/sites/${i.site_id}`) });
  if (!v) return null;
  const c = v.context;
  const resumeSymptome = [v.symptom?.trim(), (c.codes ?? []).length ? `codes ${(c.codes ?? []).join(', ')}` : null].filter(Boolean).join(' — ');
  const changerEquipement = async (equipmentId: string) => {
    try { appliquer(await api.patch<Intervention>(`/api/interventions/${i.id}`, { equipmentId: equipmentId || null })); notifier('Équipement mis à jour.'); }
    catch (e) { notifier(messageErreur(e), true); }
  };
  const puce = (k: 'frequence' | 'modification_recente' | 'depuis', val: string, lib: string) => (
    <button key={val} type="button" className={`btn int-puce ${c[k] === val ? 'btn-principal' : ''}`} aria-pressed={c[k] === val} disabled={!ecrire}
      onClick={() => auto.modifier({ context: { [k]: c[k] === val ? null : val } as any }, true)}>{lib}</button>
  );
  return (
    <details id="symptome" className="panneau int-symptome int-o-symptome" open={ouvert} onToggle={(e) => basculer((e.target as HTMLDetailsElement).open)}>
      <summary>
        <span className="pile" style={{ gap: 2, minWidth: 0, flex: 1 }}>
          <strong>Symptôme et contexte</strong>
          <span className="petit secondaire int-resume-symptome">{resumeSymptome || <span className="inconnu">Non renseigné</span>}</span>
        </span>
        <ChevronDown aria-hidden className="int-chevron" />
      </summary>
      <div className="panneau-corps pile">
        {!ecrire && <p className="petit discret">Consultation seule.</p>}
        <Champ libelle="Titre" value={v.title} disabled={!ecrire} onChange={(e) => auto.modifier({ title: e.target.value })} />
        <ChampTexte libelle="Symptôme" rows={3} value={v.symptom ?? ''} disabled={!ecrire} onChange={(e) => auto.modifier({ symptom: e.target.value })} />
        <Champ libelle="Codes défaut affichés" mono value={codesTexte} disabled={!ecrire} aide="Séparés par une virgule."
          onChange={(e) => { setCodesTexte(e.target.value); auto.modifier({ context: { codes: decouperCodes(e.target.value) } }); }} />
        <fieldset className="int-puces"><legend className="champ-libelle">Fréquence</legend>
          <div className="rangee" style={{ gap: 8 }}>{puce('frequence', 'permanente', 'Permanent')}{puce('frequence', 'intermittente', 'Intermittent')}</div></fieldset>
        <fieldset className="int-puces"><legend className="champ-libelle">Depuis quand ?</legend>
          <div className="rangee" style={{ gap: 8 }}>{DEPUIS.map((d) => puce('depuis', d, d))}</div></fieldset>
        <fieldset className="int-puces"><legend className="champ-libelle">Modification récente de l’installation ?</legend>
          <div className="rangee" style={{ gap: 8 }}>{puce('modification_recente', 'oui', 'Oui')}{puce('modification_recente', 'non', 'Non')}{puce('modification_recente', 'inconnu', 'Je ne sais pas')}</div></fieldset>
        {(c.modification_recente === 'oui' || c.modifications) && (
          <ChampTexte libelle="Modifications récentes" rows={2} value={c.modifications ?? ''} disabled={!ecrire} onChange={(e) => auto.modifier({ context: { modifications: e.target.value } })} />
        )}
        <ChampTexte libelle="Conditions d’apparition" rows={2} value={c.conditions ?? ''} disabled={!ecrire} onChange={(e) => auto.modifier({ context: { conditions: e.target.value } })} />
        <ChampTexte libelle="Alarmes et messages affichés" rows={2} value={c.alarmes ?? ''} disabled={!ecrire} onChange={(e) => auto.modifier({ context: { alarmes: e.target.value } })} />
        <ChampTexte libelle="Remarques" rows={2} value={c.remarques ?? ''} disabled={!ecrire} onChange={(e) => auto.modifier({ context: { remarques: e.target.value } })} />
        {ecrire && site.data && (
          <ChampChoix libelle="Équipement concerné" value={i.equipment_id ?? ''} onChange={(e) => changerEquipement(e.target.value)} vide="Non désigné"
            options={site.data.equipements.map((x) => ({ valeur: x.id, libelle: `${x.label}${x.reference ? ` — ${x.reference}` : ''}` }))} />
        )}
      </div>
    </details>
  );
}

// ─── Saisie : mesure, observation, hypothèse, opération, contrôle réalisé, note ───
const TYPES_SAISIE: { t: TypeElement; l: string }[] = [
  { t: 'mesure', l: 'Mesure' }, { t: 'observation', l: 'Observation' }, { t: 'hypothese', l: 'Hypothèse' },
  { t: 'controle_realise', l: 'Contrôle réalisé' }, { t: 'operation', l: 'Opération' }, { t: 'note', l: 'Note' },
];

const MESSAGES_AJOUT: Partial<Record<TypeElement, string>> = {
  mesure: 'Mesure ajoutée.', observation: 'Observation ajoutée.', hypothese: 'Hypothèse ajoutée.', controle_realise: 'Contrôle réalisé ajouté.',
  operation: 'Opération ajoutée.', note: 'Note ajoutée.',
};

function Saisie({ type, setType, actions }: { type: TypeElement; setType: (t: TypeElement) => void; actions: Actions }) {
  const [texte, setTexte] = useState('');
  const [detail, setDetail] = useState('');
  const [valeur, setValeur] = useState('');
  const [unite, setUnite] = useState('V');
  const [uniteLibre, setUniteLibre] = useState('');
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const vider = () => { setTexte(''); setDetail(''); setValeur(''); setUniteLibre(''); setErreurs({}); };

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!texte.trim()) err.texte = type === 'mesure' ? 'Indiquez le point de mesure.' : 'Texte obligatoire.';
    if (type === 'mesure') {
      if (!/^[-+]?\d+([.,]\d+)?$/.test(valeur.replace(/\s/g, ''))) err.valeur = 'Saisissez un nombre (ex. 29,6).';
      if (unite === 'autre' && !uniteLibre.trim()) err.unite = 'Précisez l’unité.';
    }
    setErreurs(err);
    if (Object.keys(err).length) return;
    const corps: Record<string, unknown> = { kind: type, text: texte.trim() };
    if (type === 'mesure') Object.assign(corps, { value: valeur.replace(/\s/g, ''), unit: unite, uniteLibre: unite === 'autre' ? uniteLibre.trim() : null });
    if (detail.trim()) corps.justification = detail.trim();
    if (await actions.ajouter(corps, MESSAGES_AJOUT[type] ?? 'Ajouté au diagnostic.')) vider();
  };

  const libelleTexte: Partial<Record<TypeElement, string>> = {
    mesure: 'Point de mesure', observation: 'Observation', hypothese: 'Hypothèse', controle_realise: 'Contrôle réalisé', operation: 'Opération réalisée', note: 'Note',
  };
  const libelleDetail: Partial<Record<TypeElement, string>> = { hypothese: 'Pourquoi cette hypothèse ?', controle_realise: 'Résultat constaté' };

  return (
    <Panneau titre="Ajouter au diagnostic">
      <form className="pile" onSubmit={soumettre} noValidate>
        <div className="int-types-saisie" role="group" aria-label="Type d’élément">
          {TYPES_SAISIE.map((x) => (
            <button key={x.t} type="button" className={`btn int-puce ${type === x.t ? 'btn-principal' : ''}`} aria-pressed={type === x.t}
              onClick={() => { setType(x.t); setErreurs({}); }}>{x.l}</button>
          ))}
        </div>
        {type === 'mesure' ? (
          <div className="int-mesure">
            <Champ libelle="Point de mesure" value={texte} onChange={(e) => setTexte(e.target.value)} erreur={erreurs.texte}
              placeholder="Ex. : bornes du bus KNX de l’alimentation" />
            <Champ libelle="Valeur" inputMode="decimal" mono value={valeur} onChange={(e) => setValeur(e.target.value)} erreur={erreurs.valeur} placeholder="29,6" />
            <ChampChoix libelle="Unité" value={unite} onChange={(e) => setUnite(e.target.value)} options={UNITES.map((u) => ({ valeur: u, libelle: u === 'autre' ? 'Autre…' : u }))} />
            {unite === 'autre' && <Champ libelle="Unité précisée" value={uniteLibre} onChange={(e) => setUniteLibre(e.target.value)} erreur={erreurs.unite} />}
          </div>
        ) : (
          <ChampTexte libelle={libelleTexte[type] ?? 'Texte'} rows={2} value={texte} onChange={(e) => setTexte(e.target.value)} erreur={erreurs.texte} />
        )}
        {libelleDetail[type] && <ChampTexte libelle={libelleDetail[type]!} rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} />}
        <div><Bouton type="submit" chargement={actions.occupe === 'ajouter'}>Ajouter</Bouton></div>
      </form>
    </Panneau>
  );
}

// ─── Chronologie ───
function Chronologie({ i, actions, ecrire, traiter }: { i: Intervention; actions: Actions; ecrire: boolean; traiter: (id: string) => void }) {
  const { confirmer, element: confirmation } = useConfirmation();
  const elements = useMemo(() => i.elements.filter((e) => e.kind !== 'securite'), [i.elements]);
  const ordre = i.elements.map((e) => e.id);
  const deplacer = (id: string, sens: -1 | 1) => {
    const k = ordre.indexOf(id);
    const j = k + sens;
    if (k < 0 || j < 0 || j >= ordre.length) return;
    const n = [...ordre]; [n[k], n[j]] = [n[j], n[k]];
    actions.ordonner(n);
  };
  const supprimer = async (el: Element) => {
    if (await confirmer('Supprimer cet élément ?', `« ${el.text.slice(0, 120)} » sera retiré du diagnostic.`, 'Supprimer l’élément')) actions.supprimer(el.id);
  };
  return (
    <Panneau serre titre="Chronologie du diagnostic" className="int-o-chrono"
      actions={<span className="visible-etroit"><LienBouton compact variante="fantome" to={`/interventions/${i.id}/rapport`} icone={<FileText aria-hidden />}>Rapport</LienBouton></span>}>
      <div className="panneau-corps-serre"><LegendeDiagnostic /></div>
      {elements.length === 0
        ? <EtatVide titre="Rien n’est encore consigné" texte="Hypothèses, contrôles, mesures et observations apparaîtront ici, dans l’ordre." />
        : (
          <ol className="int-chrono">
            {elements.map((el) => (
              <li key={el.id} className={`int-element int-element-${el.kind}`}>
                <div className="int-element-entete">
                  <EtiquetteType kind={el.kind} />
                  {el.status && <Badge ton={el.status === 'confirmee' || el.status === 'fait' ? 'succes' : el.status === 'a_faire' ? 'info' : 'neutre'} icone={false}>{LIBELLES_STATUT_ELEMENT[el.status]}</Badge>}
                  <BadgeOrigine el={el} />
                  <span className="tres-petit discret expert-seulement">{dateHeure(el.created_at)}{el.auteur ? ` — ${el.auteur}` : ''}</span>
                </div>
                <p className="int-element-texte">
                  {el.kind === 'mesure'
                    ? <>{el.text} : <strong className="mono chiffre">{valeurFr(el.value)} {el.unit}</strong></>
                    : el.text}
                </p>
                {el.justification && <p className="petit secondaire" style={{ whiteSpace: 'pre-wrap' }}>{el.kind === 'controle_realise' ? 'Résultat : ' : ''}{el.justification}</p>}
                {el.source && <SourceExtrait versionId={el.source.version_id} page={el.source.page} chunkId={el.source.chunk_id} titre={el.source.titre} version={el.source.version_label} />}
                {el.source_intervention && <LienInterventionSource s={el.source_intervention} />}
                {ecrire && (
                  <div className="groupe-btn int-element-actions">
                    {el.kind === 'controle_propose' && el.status === 'a_faire' && <Bouton compact onClick={() => traiter(el.id)}>Traiter maintenant</Bouton>}
                    {el.kind === 'hypothese' && !el.status && <>
                      <Bouton compact onClick={() => actions.majElement(el.id, { status: 'confirmee' }, 'Cause confirmée.')}>Confirmer</Bouton>
                      <Bouton compact onClick={() => actions.majElement(el.id, { status: 'ecartee' }, 'Hypothèse écartée.')}>Écarter</Bouton>
                    </>}
                    {el.kind === 'hypothese' && el.status && <Bouton compact variante="fantome" onClick={() => actions.majElement(el.id, { status: null })}>Remettre à vérifier</Bouton>}
                    {el.kind === 'controle_propose' && el.status === 'a_faire' && <span className="expert-seulement groupe-btn">
                      <button type="button" className="btn btn-fantome btn-icone" aria-label="Monter" onClick={() => deplacer(el.id, -1)}><ChevronUp aria-hidden /></button>
                      <button type="button" className="btn btn-fantome btn-icone" aria-label="Descendre" onClick={() => deplacer(el.id, 1)}><ChevronDown aria-hidden /></button>
                    </span>}
                    <button type="button" className="btn btn-fantome btn-icone int-supprimer" aria-label={`Supprimer : ${el.text.slice(0, 60)}`} onClick={() => supprimer(el)}><Trash2 aria-hidden /></button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      {confirmation}
    </Panneau>
  );
}

// ─── Clôture : résolue (cause + opérations) ou escaladée (motif) ───
function Cloture({ i, actions }: { i: Intervention; actions: Actions }) {
  const [choix, setChoix] = useState<'resolue' | 'escaladee'>(i.status === 'escaladee' ? 'escaladee' : 'resolue');
  const [resolution, setResolution] = useState(i.resolution ?? '');
  const [motif, setMotif] = useState(i.escalation ?? '');
  const { confirmer, element: confirmation } = useConfirmation();
  const causes = i.elements.filter((e) => e.kind === 'cause_confirmee');
  const operations = i.elements.filter((e) => e.kind === 'operation' && e.status !== 'a_faire');

  if (i.status === 'cloturee') return null;
  if (i.status === 'resolue' || i.status === 'escaladee') {
    const cloturer = async () => {
      if (await confirmer('Clôturer l’intervention ?', 'Une intervention clôturée n’est plus modifiable. Le rapport reste disponible.', 'Clôturer définitivement')) {
        actions.statut({ status: 'cloturee' }, 'Intervention clôturée.');
      }
    };
    return (
      <Panneau titre="Clôture" id="cloture" className="int-o-cloture">
        <div className="pile">
          {i.status === 'resolue' && <p><strong>Résolution :</strong> <Valeur v={i.resolution} inconnu="Cause confirmée, sans texte de résolution" /></p>}
          {i.status === 'escaladee' && <p><strong>Motif d’escalade :</strong> {i.escalation}</p>}
          <div className="groupe-btn">
            <Bouton variante="principal" onClick={cloturer} chargement={actions.occupe === 'statut'}>Clôturer définitivement</Bouton>
            <Bouton onClick={() => actions.statut({ status: 'en_cours' }, 'Intervention rouverte.')}>Rouvrir le diagnostic</Bouton>
          </div>
        </div>
        {confirmation}
      </Panneau>
    );
  }
  return (
    <Panneau titre="Conclure le diagnostic" id="cloture" className="int-o-cloture">
      <div className="pile">
        <div className="segment" role="group" aria-label="Issue">
          <button type="button" aria-pressed={choix === 'resolue'} onClick={() => setChoix('resolue')}>Résolue</button>
          <button type="button" aria-pressed={choix === 'escaladee'} onClick={() => setChoix('escaladee')}>Escaladée</button>
        </div>
        {choix === 'resolue' ? (
          <>
            <div className="pile" style={{ gap: 6 }}>
              <span className="champ-libelle">Cause confirmée</span>
              {causes.length ? <ul className="int-liste-simple">{causes.map((c) => <li key={c.id}>{c.text}</li>)}</ul>
                : <span className="petit inconnu">Aucune cause confirmée : décrivez la résolution ci-dessous.</span>}
              <span className="champ-libelle">Opérations réalisées</span>
              {operations.length ? <ul className="int-liste-simple">{operations.map((c) => <li key={c.id}>{c.text}</li>)}</ul>
                : <span className="petit inconnu">Aucune opération consignée (ajoutez-les avec « Opération »).</span>}
            </div>
            <ChampTexte libelle="Résolution" rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)}
              aide="Ce qui a été fait et le résultat des essais." />
            <div><Bouton variante="principal" chargement={actions.occupe === 'statut'} disabled={!causes.length && !resolution.trim()}
              onClick={() => actions.statut({ status: 'resolue', resolution: resolution.trim() || null }, 'Intervention résolue.')}>Déclarer résolue</Bouton></div>
          </>
        ) : (
          <>
            <ChampTexte libelle="Motif de l’escalade" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)}
              aide="Ce qui bloque, ce qui a été vérifié, à qui transmettre." />
            <div><Bouton variante="principal" chargement={actions.occupe === 'statut'} disabled={!motif.trim()}
              onClick={() => actions.statut({ status: 'escaladee', escalation: motif.trim() }, 'Intervention escaladée.')}>Escalader</Bouton></div>
          </>
        )}
      </div>
    </Panneau>
  );
}
